import React, { useEffect, useState } from 'react';
import { downloadCsv } from '../../utils/bulkCsv';
import { MARGIN_HEADERS, planMarginImport, readMarginFile } from '../../utils/bulkMargins';

export default function BulkMarginImport({ references, onSaveRow, disabled, productLabel, onBusyChange }) {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  useEffect(() => { onBusyChange(busy); }, [busy, onBusyChange]);
  let plans = [], validation = '';
  if (rows.length) {
    try { plans = planMarginImport(rows, references); }
    catch (err) { validation = err.message; }
  }
  const save = async () => {
    setBusy(true); setError(''); setMessage('');
    let saved = 0;
    try {
      // Validate the entire file before the first write.
      const updates = planMarginImport(rows, references);
      for (const plan of updates) {
        setProgress(`Saving ${saved + 1} of ${updates.length} margins…`);
        await onSaveRow(plan);
        saved++;
      }
      setRows([]); setMessage(`${saved} product pack margins saved.`);
    } catch (err) {
      setRows(current => current.slice(saved));
      setError(`${saved} margins saved. Remaining rows were not saved: ${err.response?.data?.message || err.message}. Retry the remaining rows after correcting the issue.`);
    } finally { setBusy(false); setProgress(''); }
  };
  return <section className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
    <h2 className="font-bold">Bulk Margin Configuration — Excel / CSV</h2>
    <p className="my-2 text-sm text-gray-700">Export Margin CSV from Inventory Stock or download this template. Fill margin_percent (10 means 10%). Each row configures a product pack for its destination and warehouse, across all batches. Blank destination_type means all destinations; blank warehouse_id means all warehouses. Blank optional settings preserve existing values; new rows default to factor 1, discount 0, and active. Upload one sheet, up to 1,000 rows, then review and save.</p>
    <div className="flex flex-wrap items-center gap-3">
      <button type="button" disabled={busy || disabled || !references.ledgers.length} className="rounded border bg-white px-3 py-2 disabled:opacity-50" onClick={() => downloadCsv('sales-ledger-reference.csv', ['sales_ledger_id', 'ledger_name', 'ledger_code', 'ledger_type'], references.ledgers.map(ledger => ({ ...ledger, sales_ledger_id: ledger.id })))}>Download Ledger Reference</button>
      <button type="button" disabled={busy || disabled} className="rounded border bg-white px-3 py-2 disabled:opacity-50" onClick={() => downloadCsv('product-pack-margins-template.csv', MARGIN_HEADERS)}>Download Margin CSV Template</button>
      <input type="file" aria-label="Upload margin Excel or CSV" accept=".xlsx,.xls,.csv" disabled={busy || disabled} onChange={async event => {
        const file = event.target.files?.[0]; event.target.value = ''; if (!file) return;
        setBusy(true); setRows([]); setError(''); setMessage('');
        try { setRows(await readMarginFile(file)); }
        catch (err) { setError(err.message); }
        finally { setBusy(false); }
      }} />
      {rows.length > 0 && <>
        <button type="button" disabled={busy || disabled || !!validation} onClick={save} className="rounded bg-emerald-700 px-3 py-2 font-semibold text-white disabled:opacity-50">Save {rows.length} Margins</button>
        <button type="button" disabled={busy || disabled} onClick={() => { setRows([]); setError(''); }} className="rounded border px-3 py-2">Clear Upload</button>
      </>}
    </div>
    <p className="mt-2 text-xs text-gray-700">sales_ledger_id accepts an existing ledger ID or a unique name/code (for example, Sales). Download Ledger Reference to see the available ledgers.</p>
    {rows.length > 0 && <div className="mt-3 max-h-80 overflow-auto rounded border bg-white">
      <table className="w-full text-left text-sm"><thead className="bg-emerald-100"><tr>{['Product pack', 'Warehouse', 'Destination', 'Margin %', 'Factor', 'Discount %', 'Action'].map(label => <th key={label} className="p-2">{label}</th>)}</tr></thead>
        <tbody>{rows.map((row, index) => <tr key={index} className="border-t">
          <td className="p-2">{productLabel(row.product_barcode_id)} <span className="text-xs">(ID: {row.product_barcode_id})</span></td>
          <td className="p-2">{row.warehouse_id || 'All'}</td><td className="p-2">{row.destination_type || 'all'} {row.dispatch_to_id || ''}</td>
          <td className="p-2">{row.margin_percent || 'Required'}</td><td className="p-2">{row.proportion_factor || 'Preserve / 1'}</td><td className="p-2">{row.discount_percent || 'Preserve / 0'}</td>
          <td className="p-2">{validation ? 'Needs validation' : plans[index]?.existingId ? 'Update' : 'Create'}</td>
        </tr>)}</tbody>
      </table>
    </div>}
    {(error || validation) && <p role="alert" className="mt-2 text-sm text-red-700">{error || validation}</p>}
    {(message || progress) && <p role="status" className="mt-2 text-sm text-emerald-800">{message || progress}</p>}
  </section>;
}
